<script lang="ts">
import { computed, onMounted, provide, shallowRef } from 'vue';
import { addMonths, add as dateAdd, eachMonthOfInterval, parse as parseDate } from 'date-fns';

import i18n from '@/localisation/i18n';
import axios from 'axios';
import Decimal from 'decimal.js';
import { flatten, isNumber, uniq } from 'lodash';

import { DataTableHeader } from 'vuetify';
import DashboardPanel from '@/modules/dashboard/components/DashboardPanel.vue';
import { formatAnyDate } from '@/utils/helpers/dates';
import { Api } from '@/modules/broker-admin/types/statistics';
import { getPriceAsString } from '@/utils/helpers/auction-numbers';
import { PRICE_PRECISION } from '@/modules/common/constants/precision';
import SimpleSecuritySearch from '@/modules/manual-loan/components/SimpleSecuritySearch.vue';
import { Security } from '@/modules/common/models';

import VChart, { THEME_KEY } from 'vue-echarts';
import { CanvasRenderer } from 'echarts/renderers';
import { LineChart } from 'echarts/charts';
import {
  GridComponent,
  LegendComponent,
  TitleComponent,
  ToolboxComponent,
  TooltipComponent,
} from 'echarts/components';
import { EChartsOption } from 'echarts';
import { use } from 'echarts/core';
import { formatPrettyNumber } from '@/modules/common/components/pretty-number';
import { UUID_EMPTY } from '@/constants';
import logger from '@/modules/common/services/logger.service';

interface VSelectOption {
  text: string;
  value: string;
  disabled?: boolean;
}

interface FeeTierOption {
  feeTier: string;
  label: string;
}

const prettyNotional = (value: Decimal | number) => getPriceAsString(value, PRICE_PRECISION);

const feeTierOptions: FeeTierOption[] = [
  { feeTier: '', label: ' - All - ' },
  { feeTier: '<1', label: 'rate < GC +1%' },
  { feeTier: '>=1', label: 'rate >= GC +1%' },
  { feeTier: '>=5', label: 'rate >= GC +5%' },
  { feeTier: '>=10', label: 'rate >= GC +10%' },
];

const sideOptions: VSelectOption[] = [
  { value: '', text: ' - All - ' },
  { value: 'BORROW', text: 'Borrower' },
  { value: 'LOAN', text: 'Lender' },
];

const groupByOptions: VSelectOption[] = [
  { value: 'feeTier', text: 'Fee Tier' },
  { value: 'company', text: 'Company' },
  { value: 'security', text: 'Security' },
];

const groupBy2Options: VSelectOption[] = [
  { value: '', text: '' },
  { value: 'side', text: 'Side' },
];

const toFromDateFormat = 'yyyy-MM';
const toLabelDateFormat = 'dd/MMM';

use([
  CanvasRenderer,
  LineChart,
  ToolboxComponent,
  GridComponent,
  TitleComponent,
  TooltipComponent,
  LegendComponent,
]);
</script>

<script setup lang="ts">
provide(THEME_KEY, 'dark');

const uuidEmpty = UUID_EMPTY;

const groupBy = shallowRef('feeTier');
const groupBy2 = shallowRef('');

const from = shallowRef('');
const fromOptions = shallowRef<string[]>([]);

const to = shallowRef('');
const toOptions = shallowRef<string[]>([]);

const lastChangedTimeFilter = shallowRef<'to' | 'from'>('to');

const company = shallowRef('');
const companyOptions = shallowRef<Array<{ id: string; name: string }>>([
  { id: '', name: ' - All - ' },
]);

const security = shallowRef<Security | null>(null);

const side = shallowRef('');
const feeTier = shallowRef('');

// series fetched from API
const series = shallowRef<Api.StatsSeries[] | null>(null);
const headers = shallowRef<Array<[string, string]>>([]);
const rows = shallowRef<Api.StatsTableRow[]>([]);

// dynamic table columns based on which groupBy values are selected
const tableColumns = computed<DataTableHeader[]>(() => [
  {
    value: 'date',
    text: i18n.t('date') as string,
    class: 'text-no-wrap text-truncate',
    align: 'start' as const,
  },

  // display the side column if we're filtering on the company
  ...(company.value !== '' || groupBy2.value === 'side'
    ? [
        {
          value: 'side',
          text: i18n.t('side') as string,
          class: 'text-no-wrap text-truncate',
          align: 'start' as const,
        },
      ]
    : []),

  // display a column for each group by value
  ...headers.value.map(([headerKey, headerValue]) => ({
    value: 'values.' + headerKey, // each row has a values = {} with a notional per header
    text: seriesName(headerValue),
    class: 'text-no-wrap text-truncate',
    align: 'end' as const,
  })),
]);

const isLoading = shallowRef(false);
const isDataAvailable = computed(() => series.value !== null && series.value.length > 0);

const chartOptions = computed(
  (): EChartsOption => ({
    backgroundColor: '#272727',
    title: {
      text: '',
    },
    legend: {
      data: series.value ? series.value.map((s) => seriesName(s.name)) : [],
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross',
      },
      valueFormatter: (value) => (isNumber(value) ? prettyNotional(value) : `${value}`),
    },
    toolbox: {
      feature: {
        saveAsImage: {},
      },
    },
    xAxis: {
      type: 'category',
      data: series.value
        ? uniq(
            flatten(
              series.value.map((s) =>
                s.data.map((row) => formatAnyDate(row.date, toLabelDateFormat))
              )
            )
          )
        : [],
    },
    yAxis: {
      type: 'value',
      name: 'notional',
      nameLocation: 'middle',
      axisLabel: {
        formatter: function (value, _index) {
          return formatPrettyNumber(value, { useGrouping: true, notation: 'compact' });
        },
      },
      nameTextStyle: { color: '#fff', padding: 30 },
    },
    series: series.value
      ? series.value.map((s) => ({
          type: 'line',
          name: seriesName(s.name),
          data: s.data.map((row) => row.notional.toNumber()),
        }))
      : [],
  })
);

const params = computed<Record<string, string | number | null>>(() => ({
  groupBy:
    groupBy.value && groupBy2.value ? `${groupBy.value},${groupBy2.value}` : `${groupBy.value}`,
  from: from.value,
  to: to.value,
  company: company.value ? company.value : null,
  security: security.value ? security.value.id : null,
  feeTier: feeTier.value ? feeTier.value : null,
  side: side.value ? side.value : null,
}));

function onChangeTimeFilter(value: 'to' | 'from'): void {
  lastChangedTimeFilter.value = value;
  fixTimeFilter();
  void fetchData();
}

function onChangeGroupBy(): void {
  groupBy2.value = '';
  switch (groupBy.value) {
    case 'feeTier':
      feeTier.value = '';
      break;
    case 'security':
      security.value = null;
      break;
    case 'company':
      company.value = '';
      side.value = '';
      break;
  }

  void fetchData();
}

function onChangeGroupBy2(): void {
  if (groupBy2.value === 'side') {
    side.value = '';
  }
  void fetchData();
}

async function fetchData(): Promise<void> {
  isLoading.value = true;
  try {
    // fetch both requests in parallel
    const [seriesData, tableData] = await Promise.all([getAllSeries(), getTableData()]);

    series.value = seriesData;
    rows.value = tableData.rows;
    headers.value = tableData.headers;
  } catch (error) {
    logger.error('Failed to fetch stats data', error);
  } finally {
    isLoading.value = false;
  }
}

async function getAllSeries(): Promise<Api.StatsSeries[] | null> {
  logger.debug(`stats/series`, params.value);

  const { data } = await axios.get<{ series: Api.StatsSeriesRaw[] | null }>(
    `/api/1/broker-user/stats/series`,
    {
      params: params.value,
    }
  );

  if (!data.series) {
    return null;
  }

  return data.series.map((s) => ({
    name: s.name,
    data: s.data.map((row) => ({
      ...row,
      date: new Date(row.date),
      notional: new Decimal(row.notional),
    })),
  }));
}

async function getTableData(): Promise<Api.StatsTableData> {
  logger.debug(`stats/table`, params.value);

  const { data } = await axios.get<Api.StatsTableDataRaw>(`/api/1/broker-user/stats/table`, {
    params: params.value,
  });

  return {
    headers: data.headers,
    rows: data.rows.map((row) => ({
      ...row,
      date: new Date(row.date),
      values: Object.fromEntries(
        Object.entries(row.values).map(([k, v]) => [k, prettyNotional(new Decimal(v))])
      ),
    })),
  };
}

async function loadFilters(): Promise<void> {
  try {
    const { data } = await axios.get<Api.StatsFilters>(`/api/1/broker-user/stats/filters`);

    companyOptions.value = [{ id: '', name: ' - All - ' }].concat(data.companies);
  } catch (error) {
    logger.error('Failed to load filters', error);
  }
}

function seriesName(rawName: string): string {
  // series name is `key=value`, but value can also contain `>=<`, so we split it on the `=`
  const [k] = rawName.split('=', 1);
  // and then take the remainder as value
  const v = rawName.substring(k.length + 1);

  switch (k) {
    case 'side':
      return v;
    case 'company':
      return displayCompany(v);
    default:
      return v;
  }
}

function displayCompany(companyId: string): string {
  const foundCompany = companyOptions.value.find((i) => i.id === companyId);
  return foundCompany ? foundCompany.name : `company:${companyId}`;
}

// ensure that from is never equal or before to by adjusting either one of them
function fixTimeFilter(): void {
  const toDate = parseDate(to.value, toFromDateFormat, new Date());
  const fromDate = parseDate(from.value, toFromDateFormat, new Date());

  if (toDate.getTime() === fromDate.getTime() || toDate.getTime() < fromDate.getTime()) {
    switch (lastChangedTimeFilter.value) {
      case 'to':
        from.value = formatAnyDate(addMonths(toDate, -1), toFromDateFormat);
        break;
      case 'from':
        to.value = formatAnyDate(addMonths(fromDate, 1), toFromDateFormat);
        break;
    }
  }
}

function formattedDate(date: Date): string {
  return formatAnyDate(date, 'yyyy/MM/dd');
}

function initializeTimeFilters(): void {
  fromOptions.value = eachMonthOfInterval({
    start: dateAdd(new Date(), { months: -12 }),
    end: new Date(),
  })
    .map((d) => formatAnyDate(d, toFromDateFormat))
    .reverse();
  toOptions.value = eachMonthOfInterval({
    start: dateAdd(new Date(), { months: -12 }),
    end: dateAdd(new Date(), { months: 1 }),
  })
    .map((d) => formatAnyDate(d, toFromDateFormat))
    .reverse();
  // use the first option for from and to fields
  from.value = fromOptions.value[1];
  to.value = toOptions.value[0];
}

onMounted(async () => {
  logger.debug(`stats mounted`);
  initializeTimeFilters();
  await loadFilters();
  await fetchData();
});
</script>

<template>
  <div>
    <DashboardPanel class="filters-panel" no-collapse title="Statistics">
      <div class="pa-4">
        <VRow>
          <!-- Grouping -->
          <VCol class="col-lg-2 col-4">
            <VRow>
              <VCol class="pb-0">
                <h4>Group By</h4>
              </VCol>
            </VRow>
            <VRow>
              <VCol>
                <VSelect
                  v-model="groupBy"
                  dense
                  :items="groupByOptions"
                  label="Main Group"
                  @change="onChangeGroupBy"
                />
              </VCol>
            </VRow>
            <VRow>
              <VCol>
                <VSelect
                  v-if="groupBy === 'company' || company !== uuidEmpty"
                  v-model="groupBy2"
                  dense
                  :items="groupBy2Options"
                  label="Subgroup"
                  @change="onChangeGroupBy2"
                />
              </VCol>
            </VRow>
          </VCol>

          <!-- Filters -->
          <VCol class="pl-6">
            <VRow>
              <VCol class="pb-0">
                <h4>Filter</h4>
              </VCol>
            </VRow>
            <VRow>
              <VCol cols="3">
                <VSelect
                  v-model="from"
                  dense
                  :items="fromOptions"
                  label="From"
                  @change="onChangeTimeFilter('from')"
                />
              </VCol>
              <VCol cols="3">
                <VSelect
                  v-model="to"
                  dense
                  :items="toOptions"
                  label="To"
                  @change="onChangeTimeFilter('to')"
                />
              </VCol>
            </VRow>
            <VRow>
              <VCol cols="3">
                <VSelect
                  v-model="company"
                  dense
                  hide-details
                  item-text="name"
                  item-value="id"
                  :items="companyOptions"
                  label="Company"
                  @change="fetchData"
                />
              </VCol>
              <VCol v-if="groupBy === 'company' || company !== uuidEmpty">
                <VSelect
                  v-model="side"
                  dense
                  hide-details
                  :items="sideOptions"
                  label="Side"
                  @change="fetchData"
                />
              </VCol>
              <VCol cols="3">
                <VSelect
                  v-model="feeTier"
                  dense
                  hide-details
                  item-text="label"
                  item-value="feeTier"
                  :items="feeTierOptions"
                  label="Fee Tier"
                  @change="fetchData"
                />
              </VCol>
              <VCol cols="3">
                <SimpleSecuritySearch
                  v-model="security"
                  dense
                  label="security"
                  @change="fetchData"
                />
              </VCol>
            </VRow>
          </VCol>
        </VRow>
      </div>
    </DashboardPanel>

    <VAlert
      v-if="!isLoading && !isDataAvailable"
      class="mt-4"
      color="primary"
      dense
      outlined
      type="warning"
    >
      There is no data available with the selected date range/filters combination.
    </VAlert>

    <DashboardPanel v-if="isDataAvailable" class="chart-panel">
      <!-- wrap the chart in a <div> to keep it from crashing -->
      <div class="chart-box">
        <VChart autoresize class="chart" :option="chartOptions" />
      </div>
    </DashboardPanel>

    <DashboardPanel v-if="isDataAvailable" class="data-panel" title="">
      <VDataTable
        class="d-flex flex flex-column"
        dense
        disable-pagination
        fixed-header
        :headers="tableColumns"
        height="100%"
        hide-default-footer
        :items="rows"
        :no-data-text="$tc('no data')"
        sort-by="date"
        sort-desc
      >
        <template #[`item.date`]="{ item }">
          <span>
            {{ formattedDate(item.date) }}
          </span>
        </template>
        <template #[`item.side`]="{ item }">
          <span>{{ item.side }}</span>
        </template>
      </VDataTable>
    </DashboardPanel>
  </div>
</template>

<style lang="scss" scoped>
::v-deep {
  .filters-panel {
    max-height: 14rem;

    // fix for above max-height being more specific
    &.is-collapsed {
      max-height: 2.7rem;
    }
  }

  .chart-panel {
    height: 360px;
    max-height: 360px;

    .panel-content {
      overflow: hidden;
    }

    .chart-box {
      .chart {
        height: 320px;
      }
    }
  }

  .data-panel {
    height: 400px;
  }

  table {
    thead th {
      text-transform: capitalize;
    }
  }
}
</style>
